import React, { useEffect } from "react";
import { Button, Spinner } from "reactstrap";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import SectionCard from "Components/Common/SectionCard";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import AdverseEventStepper from "./AdverseEventStepper";
import {
  AE_SEVERITY_OPTIONS,
  AE_CAUSALITY_OPTIONS,
  AE_ACTION_TAKEN_OPTIONS,
  AE_OUTCOME_OPTIONS,
  AE_LIKELIHOOD_OPTIONS,
  AE_RELATIONSHIP_OPTIONS,
  AE_DIAGNOSIS_OPTIONS,
} from "api/modules/common.option";
import { validations } from "validations/FormValidations";

interface AdverseEventPiAssessmentFormProps {
  control: any;
  setValue: any;
  watch: any;
  getValues: any;
  errors: any;
  clearErrors: any;
  onSubmit: (data: any) => void;
  onBack: () => void;
  onCancel: () => void;
  isPending?: boolean;
  isEditMode?: boolean;
  severityname?: string | null;
  actiontakenname?: string | null;
}

const AdverseEventPiAssessmentForm: React.FC<AdverseEventPiAssessmentFormProps> = ({
  control,
  setValue,
  watch,
  errors,
  onSubmit,
  onBack,
  isPending = false,
  isEditMode = false,
  severityname,
  actiontakenname,
}) => {
  const flatErrors: Record<string, any> = {};
  Object.keys(errors || {}).forEach((key) => {
    if (key !== "medicines" && key !== "followups") {
      flatErrors[key] = errors[key];
    }
  });

  const diagnosisVal = watch?.("diagnosis");
  const diagnosisRemarksVal = watch?.("diagnosisremarks");
  const severityGradeVal = watch?.("severitygrade");
  const actionTakenVal = watch?.("actiontaken");

  const selectedDiagnosisOption = AE_DIAGNOSIS_OPTIONS.find(
    (opt) => Number(opt.value) === Number(diagnosisVal)
  );
  const isFinalSelected =
    selectedDiagnosisOption &&
    selectedDiagnosisOption.label.toLowerCase().includes("final");

  useEffect(() => {
    if (severityGradeVal && !watch?.("piseverity")) {
      setValue?.("piseverity", Number(severityGradeVal));
    }
    if (actionTakenVal && !watch?.("piactiontaken")) {
      setValue?.("piactiontaken", Number(actionTakenVal));
    }
  }, [severityGradeVal, actionTakenVal, setValue, watch]);

  useEffect(() => {
    if (isFinalSelected && diagnosisRemarksVal) {
      setValue?.("finaldiagnosis", diagnosisRemarksVal);
    }
  }, [isFinalSelected, diagnosisRemarksVal, setValue]);

  const piAssessmentFields: FormFieldConfig[] = [
    {
      name: "finaldiagnosis",
      label: "Final Diagnosis",
      type: "text",
      required: true,
      placeholder: "Final diagnosis description...",
      disabled: !!isFinalSelected,
      rules: validations.adverseEvent.finaldiagnosis,
    },
    {
      name: "piseverity",
      label: "Severity",
      type: "select",
      required: true,
      options: AE_SEVERITY_OPTIONS,
      placeholder: "Select Severity...",
      disabled: true,
    },
    {
      name: "picausality",
      label: "Causality",
      type: "select",
      required: true,
      options: AE_CAUSALITY_OPTIONS,
      placeholder: "Select Causality...",
    },
    {
      name: "piactiontaken",
      label: "Action Taken",
      type: "select",
      required: true,
      options: AE_ACTION_TAKEN_OPTIONS,
      placeholder: "Select Action...",
      disabled: true,
    },
    {
      name: "outcomeofevent",
      label: "Outcome of Event",
      type: "select",
      required: true,
      options: AE_OUTCOME_OPTIONS,
      placeholder: "Select Outcome...",
    },
    {
      name: "likelihood",
      label: "Likelihood",
      type: "select",
      required: true,
      options: AE_LIKELIHOOD_OPTIONS,
      placeholder: "Select Likelihood...",
    },
    {
      name: "relationship",
      label: "Relationship",
      type: "select",
      required: true,
      options: AE_RELATIONSHIP_OPTIONS,
      placeholder: "Select Relationship...",
    },
  ];

  return (
    <form className={isEditMode ? formStyles.formBody : formStyles.formShell} onSubmit={onSubmit}>
      {/* Page Header (only show in Add mode to avoid duplicate stepper in Edit) */}
      {!isEditMode && (
        <>
          <div className="mb-4">
            <h4 className="mb-0 fw-700">Log New Adverse Event</h4>
          </div>

          <AdverseEventStepper activeTab="pi_assessment" />
        </>
      )}

      {/* PI / Designee Assessment Section */}
      <SectionCard
        index={1}
        icon="ri-shield-user-line"
        title="To be filled by Principal Investigator / Designee"
        subtitle="Severity, Causality, Action Taken, Outcome and Relationship assessment."
      >
        <CommonForm
          fields={piAssessmentFields}
          control={control}
          errors={flatErrors}
          columns={2}
          setValue={setValue}
        />
      </SectionCard>

      {/* Sticky Footer */}
      <div className={formStyles.stickyFooter}>
        <div className={formStyles.footerActions}>
          <div className="d-flex gap-2">
            <Button type="button" color="primary" outline onClick={onBack}>
              Back
            </Button>
          </div>
          <Button type="submit" color="primary" outline disabled={isPending}>
            {isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {isEditMode ? "Updating..." : "Saving..."}
                </>
              ) : (
                <>{isEditMode ? "Submit & Update" : "Submit & Save"}</>
              )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default AdverseEventPiAssessmentForm;
